import React, { useState, useMemo, memo, useEffect, useCallback } from 'react'

import { Pagination, Virtual, Navigation, Thumbs, Keyboard } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf,
  func,
  shape,
  string,
  oneOfType,
  number,
  bool,
} from 'prop-types'

import useBreakpoint from 'Utils/useBreakpoint'

import useCarousel from '../../useCarousel'
import LoaderOverlay from '../LoaderOverlay/LoaderOverlay'
import GenericVideoSlide from './Components/GenericVideoSlide/GenericVideoSlide'
import ImageSlide from './Components/ImageSlide/ImageSlide'
import MarmosetSlide from './Components/MarmosetSlide/MarmosetSlide'
import NavigationButtons from './Components/NavigationButtons/NavigationButtons'
import ParentalLockMedia from './Components/ParentalLockMedia/ParentalLockMedia'
import ThumbGenericVideoSlide from './Components/ThumbGenericVideoSlide/ThumbGenericVideoSlide'
import ThumbImageSlide from './Components/ThumbImageSlide/ThumbImageSlide'
import ThumbMarmosetSlide from './Components/ThumbMarmosetSlide/ThumbMarmosetSlide'
import ThumbVideoSlide from './Components/ThumbVideoSlide/ThumbVideoSlide'
import VimeoSlide from './Components/VimeoSlide/VimeoSlide'
import YouTubeSlide from './Components/YouTubeSlide/YouTubeSlide'
import { getGenericVideoThumbnailUrl } from './Utils/getGenericVideoThumbnailUrl'
import { getImageThumbnailUrl } from './Utils/getImageThumbnailUrl'
import { getMarmosetThumbnailUrl } from './Utils/getMarmosetThumbnailUrl'
import { getVideoThumbnailUrl } from './Utils/getVideoThumbnailUrl'

const TOP_CAROUSEL_SWIPER_MODULES = [Pagination, Virtual, Thumbs, Navigation, Keyboard]
const THUMBS_CAROUSEL_SWIPER_MODULES = [Pagination, Thumbs, Navigation, Keyboard]

const TOP_CAROUSEL_PAGINATION_CONFIG = {
  clickable: false,
  type: 'custom',
}

const TOP_CAROUSEL_NAVIGATION_CONFIG = {
  nextEl: '.gallery-area-top-carousel__navigation-button--next',
  prevEl: '.gallery-area-top-carousel__navigation-button--prev',
}

const THUMBS_CAROUSEL_PAGINATION_CONFIG = {
  clickable: false,
  type: 'custom',
}

const THUMBS_CAROUSEL_NAVIGATION_CONFIG = {
  nextEl: '.gallery-area-thumbs-carousel__navigation-button--next',
  prevEl: '.gallery-area-thumbs-carousel__navigation-button--prev',
}

const KEYBOARD_CONFIG = {
  enabled: true,
  onlyInViewport: true,
}

function Carousel({
  medias,
  onActiveIndexChange,
  title,
  activeIndex,
  initialIndex = 0,
  fullScreenGallery = false,
  shouldDisplayParentalLockOverlay,
  setShouldDisplayParentalLockOverlay,
  onActiveSlideDoubleClick: handleActiveSlideDoubleClick,
  extractingCoreStyles = false,
  onActiveMediaLoaded,
  isActiveMediaLoading = false,
  showAutoGeneratedImages = false,
}) {
  const [isLoadingCarousel, setIsLoadingCarousel] = useState(true)
  const [loadedMediaIndexes, setLoadedMediaIndexes] = useState(new Set())
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [hasUserInteracted, setHasUserInteracted] = useState(false)
  const breakpoint = useBreakpoint()

  const shouldRenderThumbsNavigation = useMemo(() => {
    if (extractingCoreStyles) {
      return true
    }

    if (medias.length <= 1) return false

    switch (breakpoint) {
      case 'xs':
        return false
      case 'sm':
        return medias.length > 6
      default:
        return medias.length > 8
    }
  }, [medias.length, breakpoint, extractingCoreStyles])

  useEffect(() => {
    const activeMedia = medias[activeIndex]
    const activeMediaType = activeMedia?.type
    const activeMediaVisualType = activeMedia?.visualType

    if (
      ['youtube', 'vimeo', 'marmoset'].includes(activeMediaType)
      || activeMediaVisualType === 'OpenStruct'
    ) {
      setLoadedMediaIndexes((prev) => {
        if (prev.size === 1 && prev.has(activeIndex)) return prev
        return new Set([activeIndex])
      })
    } else if (
      !['youtube', 'vimeo', 'marmoset'].includes(activeMediaType)
      && activeMediaVisualType !== 'OpenStruct'
    ) {
      setLoadedMediaIndexes((prev) => {
        if (prev.size === 0) return prev
        return new Set()
      })
    }
  }, [activeIndex, medias])

  useEffect(() => {
    const handleUserInteraction = () => {
      setHasUserInteracted(true)
    }

    const events = ['click', 'scroll', 'keydown', 'touchstart', 'mousemove', 'mousedown', 'wheel', 'mouseover']
    events.forEach((event) => {
      window.addEventListener(event, handleUserInteraction, { once: true, passive: true })
    })

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserInteraction)
      })
    }
  }, [])

  const handleMediaLoaded = useCallback((slideIndex) => {
    // Only clear loading if this is still the active slide
    if (slideIndex === activeIndex && onActiveMediaLoaded) {
      onActiveMediaLoaded()
    }
  }, [activeIndex, onActiveMediaLoaded])


  // Auto-scroll thumbnail carousel on mobile when navigating to slides that are partially visible or out of view
  useEffect(() => {
    if (!thumbsSwiperInstance || breakpoint !== 'xs') return

    const visibleIndexes = thumbsSwiperInstance.visibleSlidesIndexes
    if (!visibleIndexes || visibleIndexes.length === 0) return

    const lastVisibleIndex = visibleIndexes[visibleIndexes.length - 1]

    if (activeIndex >= lastVisibleIndex) {
      if (thumbsSwiperInstance.params.loop) {
        thumbsSwiperInstance.slideToLoop(activeIndex, 300)
      } else {
        thumbsSwiperInstance.slideTo(activeIndex, 300)
      }
    }
  }, [activeIndex, thumbsSwiperInstance, breakpoint])

  const {
    swiperRef,
    handleSlideChange,
  } = useCarousel({
    medias,
    onActiveIndexChange,
    activeIndex,
    baseClassName: 'gallery-area-top-carousel',
    isTransitioning,
  })

  const [thumbsSwiperInstance, setThumbsSwiperInstance] = useState(null)

  const handleAfterInit = useCallback(() => {
    setIsLoadingCarousel(false)
  }, [])

  const handleTransitionEnd = useCallback(() => {
    setIsTransitioning(false)
  }, [])

  const handleTransitionStart = useCallback(() => {
    setIsTransitioning(true)
  }, [])

  const handleSwiperDoubleClick = useCallback((swiper, event) => {
    if (shouldDisplayParentalLockOverlay || isTransitioning) {
      return
    }

    // Check if the click is within the swiper container (works with virtual slides + loop)
    const swiperContainer = swiper.el
    if (!swiperContainer?.contains(event.target)) {
      return
    }

    handleActiveSlideDoubleClick()
  }, [shouldDisplayParentalLockOverlay, isTransitioning, handleActiveSlideDoubleClick])

  const thumbsConfig = useMemo(() => ({
    swiper: thumbsSwiperInstance,
  }), [thumbsSwiperInstance])

  const renderedSlides = useMemo(() => {
    if (!medias.length) {
      return null
    }

    return medias.map((media, index) => {
      const {
        visualType,
        type,
        externalId,
        fileUrl,
      } = media

      const isActiveSlide = index === activeIndex && loadedMediaIndexes.has(index)

      if (visualType === 'ItemImage') {
        return (
          <SwiperSlide key={`gallery-area-top-carousel-slide-${index}`}>
            <ImageSlide
              media={media}
              title={title}
              isFirstImage={index === 0}
              slideIndex={index}
              onLoad={handleMediaLoaded}
              onError={handleMediaLoaded}
            />
          </SwiperSlide>
        )
      }

      if (type === 'youtube') {
        return (
          <SwiperSlide
            key={`gallery-area-top-carousel-slide-${index}`}
            className="gallery-area-top-carousel__video-slide"
          >
            <YouTubeSlide
              externalId={externalId}
              isActiveSlide={isActiveSlide}
              slideIndex={index}
              onLoad={handleMediaLoaded}
            />
          </SwiperSlide>
        )
      }

      if (type === 'vimeo') {
        return (
          <SwiperSlide
            key={`gallery-area-top-carousel-slide-${index}`}
            className="gallery-area-top-carousel__video-slide"
          >
            <VimeoSlide
              externalId={externalId}
              isActiveSlide={isActiveSlide}
              slideIndex={index}
              onLoad={handleMediaLoaded}
            />
          </SwiperSlide>
        )
      }

      if (type === 'marmoset') {
        return (
          <SwiperSlide
            className="swiper-no-swiping"
            key={`gallery-area-top-carousel-slide-${index}`}
          >
            <MarmosetSlide fileUrl={fileUrl} />
          </SwiperSlide>
        )
      }

      if (visualType === 'OpenStruct') {
        return (
          <SwiperSlide
            key={`gallery-area-top-carousel-slide-${index}`}
            className="gallery-area-top-carousel__video-slide"
          >
            <GenericVideoSlide
              fileUrl={fileUrl}
              isActiveSlide={isActiveSlide}
              slideIndex={index}
              onLoad={handleMediaLoaded}
            />
          </SwiperSlide>
        )
      }

      return null
    })
  }, [activeIndex, loadedMediaIndexes, handleMediaLoaded, medias, title, hasUserInteracted])

  const renderFirstSlide = useCallback((media) => {
    if (!media) {
      return null
    }

    const { visualType } = media

    if (visualType === 'ItemImage') {
      return (
        <ImageSlide
          media={media}
          title={title}
          isFirstImage
        />
      )
    }

    return null
  }, [title])

  const renderedThumbs = useMemo(() => {
    if (shouldDisplayParentalLockOverlay) {
      return null
    }

    return medias.map((media, index) => {
      const { visualType, type } = media
      const isAutoGenerated = Boolean(showAutoGeneratedImages && media.autoGenerated)

      if (visualType === 'ItemImage') {
        const src = getImageThumbnailUrl(media)

        return (
          <SwiperSlide key={`gallery-area-thumbs-carousel-slide-${index}`}>
            <ThumbImageSlide
              src={src}
              index={index}
              isAutoGenerated={isAutoGenerated}
            />
          </SwiperSlide>
        )
      }

      if (type === 'youtube' || type === 'vimeo') {
        const src = getVideoThumbnailUrl(media)

        return (
          <SwiperSlide key={`gallery-area-thumbs-carousel-slide-${index}`}>
            <ThumbVideoSlide src={src} />
          </SwiperSlide>
        )
      }

      if (type === 'marmoset') {
        const src = getMarmosetThumbnailUrl(medias)

        return (
          <SwiperSlide key={`gallery-area-thumbs-carousel-slide-${index}`}>
            <ThumbMarmosetSlide src={src} />
          </SwiperSlide>
        )
      }

      if (visualType === 'OpenStruct') {
        const src = getGenericVideoThumbnailUrl(medias)

        return (
          <SwiperSlide key={`gallery-area-thumbs-carousel-slide-${index}`}>
            <ThumbGenericVideoSlide src={src} />
          </SwiperSlide>
        )
      }

      return null
    })
  }, [shouldDisplayParentalLockOverlay, medias, showAutoGeneratedImages])

  return (
    <div className={classnames(
      'gallery-area-carousel',
      fullScreenGallery && 'gallery-area-carousel--hidden'
    )}>
      {isLoadingCarousel && (
        <div className="gallery-area-top-carousel gallery-area-top-carousel--first-slide">
          <div className="swiper-wrapper">
            <div className="swiper-slide">
              {renderFirstSlide(medias[0])}
            </div>
          </div>
        </div>
      )}
      <Swiper
        id="gallery-area-top-carousel"
        ref={swiperRef}
        modules={TOP_CAROUSEL_SWIPER_MODULES}
        className="gallery-area-top-carousel"
        slidesPerView={1}
        lazyPreloadPrevNext={1}
        pagination={TOP_CAROUSEL_PAGINATION_CONFIG}
        navigation={TOP_CAROUSEL_NAVIGATION_CONFIG}
        keyboard={KEYBOARD_CONFIG}
        initialSlide={initialIndex}
        allowTouchMove
        loop
        spaceBetween={0}
        direction="horizontal"
        thumbs={thumbsConfig}
        onSlideChange={handleSlideChange}
        onAfterInit={handleAfterInit}
        onTransitionEnd={handleTransitionEnd}
        onTransitionStart={handleTransitionStart}
        onDoubleClick={handleSwiperDoubleClick}
        virtual
      >
        {shouldDisplayParentalLockOverlay ? (
          <SwiperSlide className="swiper--parental-lock-media">
            <ParentalLockMedia
              setShouldDisplayParentalLockOverlay={setShouldDisplayParentalLockOverlay}
            />
          </SwiperSlide>
        ) : renderedSlides
        }
        <NavigationButtons
          className="gallery-area-carousel__navigation-buttons"
          prevClass="gallery-area-top-carousel__navigation-button--prev"
          nextClass="gallery-area-top-carousel__navigation-button--next"
        />
        <LoaderOverlay
          isLoading={isActiveMediaLoading && !shouldDisplayParentalLockOverlay}
        />
      </Swiper>
      <div className="gallery-area-thumbs-carousel-container">
        <Swiper
          onSwiper={setThumbsSwiperInstance}
          modules={THUMBS_CAROUSEL_SWIPER_MODULES}
          className="gallery-area-thumbs-carousel"
          slidesPerView="auto"
          slidesPerGroupAuto
          pagination={THUMBS_CAROUSEL_PAGINATION_CONFIG}
          spaceBetween={0}
          slideToClickedSlide
          direction="horizontal"
          navigation={THUMBS_CAROUSEL_NAVIGATION_CONFIG}
        >
          {renderedThumbs}
        </Swiper>
        {shouldRenderThumbsNavigation && (
          <NavigationButtons
            className="gallery-area-thumbs-carousel__navigation-buttons"
            prevClass="gallery-area-thumbs-carousel__navigation-button--prev"
            nextClass="gallery-area-thumbs-carousel__navigation-button--next"
          />
        )}
      </div>
    </div>
  )
}

Carousel.propTypes = {
  title: string.isRequired,
  medias: arrayOf(shape({
    externalId: string,
    image: oneOfType([
      string,
      shape({
        thumb: string,
      }),
    ]),
    imageName: string,
    imageNameThumb: string,
    model: shape({
      imageName: shape({
        thumb: shape({
          url: string,
        }),
        large: shape({
          url: string,
        }),
      }),
    }),
    type: string,
    fileUrl: string,
    visualType: string,
  })).isRequired,
  onActiveIndexChange: func.isRequired,
  activeIndex: number.isRequired,
  initialIndex: number,
  fullScreenGallery: bool,
  shouldDisplayParentalLockOverlay: bool.isRequired,
  setShouldDisplayParentalLockOverlay: func.isRequired,
  onActiveSlideDoubleClick: func.isRequired,
  extractingCoreStyles: bool,
  onActiveMediaLoaded: func,
  isActiveMediaLoading: bool,
  showAutoGeneratedImages: bool,
}

export default memo(Carousel)
